import { create } from 'zustand'

export enum Role {
  Admin = 'admin',
  Builder = 'builder',
  Developer = 'developer',
  Operator = 'operator',
  Viewer = 'viewer',
}

export type Capability =
  | 'read:recipes'
  | 'write:recipes'
  | 'deploy:recipes'
  | 'read:executions'
  | 'write:executions'
  | 'read:logs'
  | 'manage:users'

interface PermissionStore {
  role: Role
  capabilities: Set<Capability>
  setRole: (role: Role) => void
  hasCapability: (capability: Capability) => boolean
  addCapability: (capability: Capability) => void
  removeCapability: (capability: Capability) => void
}

const roleCapabilities: Record<Role, Capability[]> = {
  [Role.Admin]: [
    'read:recipes',
    'write:recipes',
    'deploy:recipes',
    'read:executions',
    'write:executions',
    'read:logs',
    'manage:users',
  ],
  [Role.Builder]: [
    'read:recipes',
    'write:recipes',
    'deploy:recipes',
    'read:executions',
    'read:logs',
  ],
  [Role.Developer]: [
    'read:recipes',
    'write:recipes',
    'deploy:recipes',
    'read:executions',
    'read:logs',
  ],
  [Role.Operator]: ['read:recipes', 'read:executions', 'write:executions', 'read:logs'],
  [Role.Viewer]: ['read:recipes', 'read:executions', 'read:logs'],
}

export const usePermissionStore = create<PermissionStore>((set, get) => ({
  role: Role.Viewer,
  capabilities: new Set(),
  setRole: (role) =>
    set({
      role,
      capabilities: new Set(roleCapabilities[role]),
    }),
  hasCapability: (capability) => {
    return get().capabilities.has(capability)
  },
  addCapability: (capability) =>
    set((state) => {
      const newCaps = new Set(state.capabilities)
      newCaps.add(capability)
      return { capabilities: newCaps }
    }),
  removeCapability: (capability) =>
    set((state) => {
      const newCaps = new Set(state.capabilities)
      newCaps.delete(capability)
      return { capabilities: newCaps }
    }),
}))
